import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Shield, Headphones, Cloud, Wrench, Cpu, Lock, ArrowRight, CheckCircle2, Phone, Mail, MapPin, Search, ShieldCheck, Scale, Home, LineChart, Smartphone, HardDrive, MessageSquare, BarChart3, MonitorCheck, FileBarChart, Code2, MonitorSmartphone, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import simpletechLogo from "@/assets/simpletech-logo-transparent.png";
import simpletechLogoFooter from "@/assets/simpletech-logo-footer.png";
import heroBg from "@/assets/hero-bg.jpg";
import logoBni from "@/assets/logos/bni.webp";
import logoAllstate from "@/assets/logos/allstate.webp";
import logoBenFranklin from "@/assets/logos/benjamin-franklin.webp";
import logoHopeThrive from "@/assets/logos/hope-thrive.webp";
import logoCme from "@/assets/logos/cme.webp";
import logoHharpp from "@/assets/logos/hharpp.webp";
import logoSimone from "@/assets/logos/simone-ravicz.webp";
import logoFarmers from "@/assets/logos/farmers.webp";
import logoNewlife from "@/assets/logos/newlife.webp";
import dotsDecoration from "@/assets/dots-decoration.png";
import dotsDecorationRight from "@/assets/dots-decoration-right.png";
import armandsHeadshot from "@/assets/armands-headshot.webp";
import industriesBg from "@/assets/industries-bg.jpg";
import mapsDotsBg from "@/assets/bg-maps-dots.jpg";
import { LazyBookingIframe } from "@/components/LazyBookingIframe";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";


const partnerLogos = [
  { src: logoBni, alt: "BNI" },
  { src: logoAllstate, alt: "Allstate" },
  { src: logoBenFranklin, alt: "Benjamin Franklin Plumbing" },
  { src: logoHopeThrive, alt: "Hope & Thrive Counseling" },
  { src: logoCme, alt: "CME Consultants" },
  { src: logoHharpp, alt: "Hharpp Business Services" },
  { src: logoSimone, alt: "Simone Ravicz, Ph.D." },
  { src: logoFarmers, alt: "Farmers Insurance" },
  { src: logoNewlife, alt: "Newlife" },
];

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "IT Support Montclair CA | SimpleTech — (909) 255-9112" },
      { name: "description", content: "Downtime costs Montclair, CA businesses billable hours. SimpleTech Helper provides managed IT, cybersecurity, and VoIP with local on-site response." },
      { property: "og:title", content: "IT Support Montclair CA | SimpleTech — (909) 255-9112" },
      { property: "og:description", content: "Downtime costs Montclair, CA businesses billable hours. SimpleTech Helper provides managed IT, cybersecurity, and VoIP with local on-site response." },
      { name: "twitter:title", content: "IT Support Montclair CA | SimpleTech — (909) 255-9112" },
      { name: "twitter:description", content: "Downtime costs Montclair, CA businesses billable hours. SimpleTech Helper provides managed IT, cybersecurity, and VoIP with local on-site response." },

      seoOgUrl("/"),
    ],
    links: [
      seoCanonicalLink("/"),
      // Preload the hero background so the LCP element is discoverable in the
      // initial HTML instead of waiting on CSS parsing.
      { rel: "preload", as: "image", href: heroBg, fetchpriority: "high" } as unknown as { rel: string; href: string },
    ],
  }),
});

const services = [
  { icon: Headphones, title: "24/7 Helpdesk", desc: "Talk to a real engineer in under 90 seconds — day or night, weekends included." },
  { icon: Shield, title: "Cybersecurity", desc: "Endpoint protection, threat monitoring, phishing simulations, and incident response." },
  { icon: Cloud, title: "Cloud & Microsoft 365", desc: "Migration, management, and optimization of Azure, AWS, and Microsoft 365 tenants." },
  { icon: Wrench, title: "Onsite Repairs", desc: "Hardware diagnostics, network installations, and break-fix service across the region." },
  { icon: Cpu, title: "Managed Infrastructure", desc: "Servers, networks, and backup — proactively monitored so problems are fixed before you notice." },
  { icon: Lock, title: "Compliance", desc: "Cyber Essentials, ISO 27001, and GDPR readiness with audit-ready reporting." },
];

const stats = [
  { value: "<2 min", label: "Avg response time" },
  { value: "99.98%", label: "Uptime guaranteed" },
  { value: "12 yrs", label: "Serving SMBs" },
  { value: "340+", label: "Active clients" },
];

const HERO_HEADLINE = "Managed IT Support & Cybersecurity for Montclair and the Inland Empire";

function Index() {
  const trustIndexRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!trustIndexRef.current) return;
    const existing = trustIndexRef.current.querySelector('script[data-trustindex]');
    if (existing) return;
    // Defer non-critical third-party widget until the browser is idle so it
    // does not contend with the main thread during initial interaction.
    const load = () => {
      if (!trustIndexRef.current) return;
      const script = document.createElement('script');
      script.defer = true;
      script.async = true;
      script.dataset.trustindex = 'true';
      script.src = 'https://cdn.trustindex.io/loader.js?766f73524412630f62168b4ef37';
      trustIndexRef.current.appendChild(script);
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback ? w.requestIdleCallback(load) : window.setTimeout(load, 1500);
    return () => {
      const w2 = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (w2.cancelIdleCallback) w2.cancelIdleCallback(id as number);
      else clearTimeout(id as number);
    };
  }, []);


  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="home" />

      <main id="main-content">


      {/* Hero */}
      <section id="top" className="relative overflow-hidden bg-neutral-950">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBg})` }}
        />

        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-primary-foreground">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.05]">
              {HERO_HEADLINE}
            </h1>
            <div className="mt-10">
              <Link
                to="/services"
                className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                VIEW SERVICES
              </Link>
            </div>
            <p className="mt-8 text-lg md:text-xl text-primary-foreground/90 max-w-2xl">
              Top-Rated Tech Support for Businesses, Consumers, and Seniors – Delivered with Care, Clarity, and Local Expertise
            </p>
          </div>
        </div>
      </section>

      {/* Partner logos marquee */}
      <section className="py-12 border-b border-border overflow-hidden" style={{ backgroundColor: "#f8f8f8" }}>
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Trusted by local businesses & professionals
          </p>
        </div>
        <div
          className="relative w-full overflow-hidden"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="flex w-max animate-marquee gap-16 items-center">
            {[...partnerLogos, ...partnerLogos].map((logo, i) => (
              <img
                key={`${logo.alt}-${i}`}
                src={logo.src}
                alt={logo.alt}
                width={200}
                height={64}
                loading="lazy"
                decoding="async"
                className="h-14 md:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="relative bg-white pt-10 md:pt-16 overflow-hidden">
        <img
          src={dotsDecoration}
          alt=""
          aria-hidden="true"
          width={260}
          height={315}
          loading="lazy"
          decoding="async"
          className="pointer-events-none select-none absolute left-0 top-0 w-[260px] max-w-[22%] opacity-90 h-auto"
        />
        <img
          src={dotsDecorationRight}
          alt=""
          aria-hidden="true"
          width={300}
          height={414}
          loading="lazy"
          decoding="async"
          className="pointer-events-none select-none absolute right-0 bottom-0 w-[300px] max-w-[24%] opacity-90 h-auto"
        />
        <div className="relative w-full grid lg:grid-cols-2 gap-8 items-end">
          <div className="pl-10 md:pl-20 lg:pl-[26%] pr-6 pb-14 md:pb-20">
            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-5" style={{ color: "#7141b1" }}>
              OUR COMMITMENT TO OUR CUSTOMERS
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15] text-slate-900 mb-8">
              We Make IT Services in Southern California Easy For Everyone… Even Seniors!
            </h2>
            <div className="space-y-5 text-slate-700 text-base md:text-lg leading-relaxed">
              <p>At the heart of our company is you – our customer. If anything tech related has been causing a headache, let us help!</p>
              <p>Our team is on standby and ready to resolve any issue that might be holding your business (or life) back so you can keep running smoothly.</p>
              <p>We're committed to giving outstanding service and support every step of the way and we'll always have your back when it counts most.</p>
            </div>
            <div className="mt-10">
              <Link
                to="/services"
                className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                VIEW OUR SERVICES
              </Link>
            </div>
          </div>
          <div className="relative flex justify-end items-end">
            <img
              src={armandsHeadshot}
              alt="Armands Williams, friendly SimpleTech IT specialist ready to help businesses, families, and seniors in Southern California"
              width={640}
              height={776}
              loading="lazy"
              decoding="async"
              className="relative z-10 w-full max-w-[640px] h-auto object-contain object-bottom translate-x-[-35%]"
            />

          </div>
        </div>
      </section>

      {/* Industries */}
      <section
        id="services"
        className="relative py-24 md:py-32 bg-[#0b0f3d] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${industriesBg})` }}
      >
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.3em] text-white/60 font-semibold mb-4">
              MY AREAS
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.15]">
              Industries I've Supported<br />and Solutions Delivered
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-7">
            {[
              {
                num: "01",
                icon: ShieldCheck,
                title: "Insurance Agents",
                desc: "One of my insurance clients was struggling to keep their operations compliant and organized. Their devices needed regular checks to meet industry standards, and employees often faced delays in getting the equipment they needed. I stepped in to ensure all their devices were fully compliant and took over the distribution of office equipment. Now, their teams have what they need, when they need it, and their operations run smoothly without any compliance worries.",
                link: "/services/tech-support-for-insurance-agents",
              },
              {
                num: "02",
                icon: Scale,
                title: "Estate Planners / Attorneys",
                desc: "Many estate planners and attorneys rely on outdated software, which can hinder their productivity. I recently worked with a client struggling to keep their systems running smoothly. After updating their computers and optimizing their technology workflow, I ensured their legacy software seamlessly integrated with modern tools. Now, they can focus on their clients without worrying about technical disruptions.",
                link: "/services/tech-support-for-estate-planning-attorneys",
              },
              {
                num: "03",
                icon: Home,
                title: "Realtors",
                desc: "One of my clients, a busy real estate firm, was overwhelmed by the complexity of their software systems. They felt burdened by unnecessary technology that distracted them from serving their clients. I helped simplify their tech stack, focusing on tools that truly mattered for their day-to-day operations. Now, they have a clean, efficient setup that lets them focus on closing deals and growing their business.",
                link: "/services/tech-support-for-realtors",
              },
              {
                num: "04",
                icon: LineChart,
                title: "Financial Advisors",
                desc: "A financial agency I work with was struggling to manage their communication systems while staying compliant with industry regulations. I set up custom email solutions tailored to their needs and worked with them through every step to ensure compliance was always a priority. Now, their communication runs smoothly, securely, and in full compliance with all regulatory requirements.",
                link: "/services/tech-support-for-financial-advisors",
              },
            ].map(({ num, icon: Icon, title, desc, link }: { num: string; icon: typeof Home; title: string; desc: string; link?: string }) => {
              const inner = (
                <>
                  <span className="absolute -top-3 -left-1 text-7xl md:text-8xl font-extrabold leading-none text-sky-400 tracking-tight select-none">
                    {num}
                  </span>
                  <div className="mb-5 pl-24 md:pl-28">
                    <h3 className="text-2xl md:text-[26px] font-extrabold text-slate-900 group-hover:text-white transition-colors duration-300 leading-tight">
                      {title}
                    </h3>
                  </div>
                  <p className="text-slate-700 group-hover:text-white transition-colors duration-300 text-[15px] leading-relaxed text-center">
                    {desc}
                  </p>
                  {/* Corner accent with icon */}
                  <div
                    className="absolute bottom-0 right-0 w-24 h-24 flex items-center justify-center text-white bg-[#332d5f] group-hover:bg-[#28bcff] transition-colors duration-300"
                    style={{ borderTopLeftRadius: "100%" }}
                  >
                    <div className="translate-x-3 translate-y-3">
                      <Icon className="w-7 h-7" strokeWidth={2} />
                    </div>
                  </div>
                </>
              );
              const cls =
                "group relative bg-white hover:bg-transparent transition-colors duration-300 rounded-md overflow-hidden px-8 md:px-10 pt-16 md:pt-20 pb-20";
              return link ? (
                <Link key={num} to={link} className={cls}>
                  {inner}
                </Link>
              ) : (
                <div key={num} className={cls}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Secure CTA */}
      <section className="relative bg-white py-24 md:py-32 overflow-hidden">
        <img
          src={dotsDecoration}
          alt=""
          aria-hidden="true"
          width={300}
          height={364}
          loading="lazy"
          decoding="async"
          className="pointer-events-none select-none absolute left-0 top-0 w-[300px] max-w-[24%] opacity-90 h-auto"
        />
        <img
          src={dotsDecorationRight}
          alt=""
          aria-hidden="true"
          width={300}
          height={414}
          loading="lazy"
          decoding="async"
          className="pointer-events-none select-none absolute right-0 bottom-0 w-[300px] max-w-[24%] opacity-90 h-auto"
        />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-6">
            Ready to Secure Your Business?
          </h2>
          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-10">
            For a limited time, Montclair businesses get a <strong className="font-bold">FREE 30-minute VoIP consultation</strong>—valued at $199! Discover how a modern phone system can save money and boost efficiency. Offer valid through July 31, 2026
          </p>
          <Link
            to="/contact"
            data-ga-event="booking_click"
            className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            BOOK YOUR FREE CONSULTATION NOW!
          </Link>
        </div>
      </section>

      {/* What can we do */}
      <section
        id="why"
        className="relative py-24 md:py-32 bg-[#0b0f3d] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${industriesBg})` }}
      >
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold mb-4">
                WE SOLVE REAL PROBLEMS
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                What can we do for you?
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-block self-start lg:self-auto bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
            >
              MORE SERVICES
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {[
              { icon: Cpu, title: "IT Support", desc: "Comprehensive tech support solutions tailored to each individual customer's needs.", link: "/tech-support-montclair-ca" },
              { icon: Smartphone, title: "Managed IT", desc: "Reliable managed IT services built to maintain seamless operations and protect your business around the clock.", link: "/services/managed-it-services" },
              { icon: HardDrive, title: "Backup & Recovery", desc: "Keep your data secure and accessible with SimpleTech's powerful backup and recovery solutions.", link: "/services/managed-it-services" },
              { icon: MessageSquare, title: "VoIP", desc: "Streamline your business communication with customized, secure, and reliable VoIP solutions—built to be cost-effective and easy to manage.", link: "/services/voip" },
              { icon: BarChart3, title: "Software solution", desc: "Seamless software integration that connects your tools, automates workflows, and improves efficiency—so your business runs smarter, not harder.", link: "/services/software-integration" },
              { icon: MonitorCheck, title: "Cyber security", desc: "Protect your systems and data with advanced firewalls, encryption, real-time threat detection, and targeted employee training.", link: "/services/cyber-security" },
              { icon: FileBarChart, title: "HIPAA compliance", desc: "We help healthcare practices achieve and maintain full HIPAA compliance with confidence and ease.", link: "/services/cyber-security" },
              { icon: Code2, title: "Website", desc: "We are passionate about creating websites that will give our clients more visibility online.", link: "/services/website-design" },
              { icon: MonitorSmartphone, title: "Senior Tech Support", desc: "Friendly, patient tech support for seniors—helping you stay connected, solve tech issues, and use your devices with confidence.", link: "/services/tech-support-for-seniors" },
            ].map(({ icon: Icon, title, desc, link }) => (
              <Link key={title} to={link} className="flex gap-5 group hover:opacity-80 transition-opacity">
                <div className="flex-shrink-0 text-sky-400 group-hover:text-sky-300 transition-colors">
                  <Icon className="w-12 h-12" strokeWidth={1.4} />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-extrabold text-white mb-3 group-hover:text-sky-300 transition-colors">{title}</h3>
                  <p className="text-white/80 text-[15px] leading-relaxed">{desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Giving Back */}
      <section className="relative overflow-hidden py-24 md:py-32 bg-gradient-to-br from-[#0a0e2e] via-[#0b0f3d] to-[#141432]">
        {/* Decorative blurred orbs */}
        <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#28bcff]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Video */}
            <div className="relative lg:col-span-3">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-sky-400/20 via-[#28bcff]/15 to-blue-500/20 blur-xl" aria-hidden="true" />
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
                <YouTubeFacade
                  videoId="ZDA6NeRWmHE"
                  title="Newlife Pomona Food Bank"
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sky-300/80 font-semibold">
                <Sparkles className="w-4 h-4" />
                In partnership with Newlife Pomona Food Bank
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-400/10 px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase text-sky-300 mb-6 ring-1 ring-sky-400/20">
                <Heart className="w-3.5 h-3.5 fill-sky-400 text-sky-400" />
                Tech with Purpose
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] mb-7">
                Where your <span className="italic font-serif text-sky-400">"yes"</span> meets a family's need.
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                A portion of every project we complete goes straight to <strong className="text-white">Newlife Pomona Food Bank</strong> — putting groceries on tables for neighbors who need them most.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed mb-10">
                When you partner with us for tech support, you're not just fixing computers. You're feeding families, fueling hope, and writing your story into someone else's miracle.
              </p>

              {/* Impact strip */}
              <div className="grid grid-cols-3 gap-4 mb-10 border-y border-sky-400/20 py-6">
                {[
                  { value: "5%", label: "of every project donated" },
                  { value: "1,200+", label: "meals funded to date" },
                  { value: "100%", label: "local impact" },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-3xl md:text-4xl font-extrabold text-sky-400 leading-none">{s.value}</div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mt-2 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://www.youtube.com/watch?v=ZDA6NeRWmHE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-[#0b0f3d] font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  WATCH THEIR STORY
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="https://pushpay.com/g/newlifepomona?r=monthly&src=hpp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#28bcff] to-sky-500 hover:from-sky-400 hover:to-sky-600 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-all shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50"
                >
                  <Heart className="w-4 h-4 fill-white" />
                  DONATE NOW
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials + Consultation share one world-map background */}
      <div
        className="relative bg-slate-50 bg-no-repeat bg-top bg-cover"
        style={{ backgroundImage: `url(${mapsDotsBg})` }}
      >
      {/* Testimonials */}
      <section id="reviews" className="relative py-24 md:py-32 overflow-hidden scroll-mt-24">
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-sky-600 mb-3">From Our Clients</div>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#0b0f3d]">Testimonials</h2>
          </div>

          {/* Tabs + rating bar */}
          <div ref={trustIndexRef} className="max-w-6xl mx-auto" />
        </div>
      </section>

      {/* Consultation */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0b0f3d] leading-[1.05] mb-6">
            Do You Want More From Your IT Support Company?
          </h2>
          <p className="text-xl md:text-2xl text-[#28bcff] font-semibold mb-12">
            Schedule Your Initial Consultation Now
          </p>

          <div className="mx-auto max-w-4xl">
            <LazyBookingIframe />
          </div>
        </div>
      </section>
      </div>
      {/* end shared world-map background */}
      </main>





      <footer className="bg-[#0b0f3d] text-white pt-16 pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pb-12">
            {/* Logo + socials */}
            <div className="flex flex-col items-start">
              <img src={simpletechLogoFooter} alt="SimpleTech logo — trusted IT support, cybersecurity, and managed services for Southern California businesses" width={160} height={53} className="w-40 h-auto mb-8" />
              <div className="flex items-center gap-3">
                {[
                  { label: "Facebook", initial: "f" },
                  { label: "Instagram", initial: "" },
                  { label: "LinkedIn", initial: "in" },
                  { label: "Google", initial: "G" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    aria-label={s.label}
                    className="w-9 h-9 rounded-full bg-white text-[#0b0f3d] grid place-items-center text-sm font-bold hover:bg-[#28bcff] hover:text-white transition-colors"
                  >
                    {s.initial}
                  </a>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-extrabold mb-6">Services</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm text-slate-200">
              <Link to="/services" className="hover:text-[#28bcff]">Tech Support</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Backup and Recovery</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Managed IT</Link>
                <Link to="/services" className="hover:text-[#28bcff]">HIPPA Compliance</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Cyber Security</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Website Design</Link>
                <Link to="/services" className="hover:text-[#28bcff]">VoIP</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Senior Tech Support</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Software Solution</Link>
              </div>
            </div>

            {/* Important */}
            <div>
              <h3 className="text-2xl font-extrabold mb-6">Important</h3>
              <div className="flex flex-col gap-4 text-sm text-slate-200">
                <Link to="/" className="hover:text-[#28bcff]">Home</Link>
                <Link to="/about" className="hover:text-[#28bcff]">About</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Services</Link>
                <Link to="/" hash="reviews" className="hover:text-[#28bcff]">Reviews</Link>
                <Link to="/contact" className="hover:text-[#28bcff]">Contact</Link>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-7 text-sm">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Call Us</div>
                  <a href="tel:9092559112" className="text-slate-200 hover:text-[#28bcff]">(909) 255-9112</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Email Us</div>
                  <a href="mailto:contact@simpletechhelper.com" className="text-slate-200 hover:text-[#28bcff] break-all">contact@simpletechhelper.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Mailing Address</div>
                  <div className="text-slate-200 leading-relaxed">
                    8880 N Benson Ave #121<br />
                    Montclair, CA 91763
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-slate-300">
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/privacy-policy" className="hover:text-[#28bcff]">Privacy Policy</Link>
              <span className="text-white/20">|</span>
              <Link to="/terms-and-conditions" className="hover:text-[#28bcff]">Terms and Conditions</Link>
              <span className="text-white/20">|</span>
              <Link to="/accessibility-statement" className="hover:text-[#28bcff]">Accessibility</Link>
              <span className="text-white/20">|</span>
              <Link to="/sitemap" className="hover:text-[#28bcff]">Sitemap</Link>
            </div>
            <div>Copyright © {new Date().getFullYear()} SimpleTech. All Rights Reserved.</div>
          </div>

          <div className="text-center mt-10 pt-8 border-t border-white/10">
            <p className="text-slate-200 text-base">
              For in him we live, and move, and have our being; as certain also of your own poets have said, For we are also his offspring.
            </p>
            <p className="text-slate-400 tracking-widest text-xs mt-3 uppercase">Acts 17:28</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
